import React from 'react';
import { 
  Card, Tabs, Empty, Spin, Tag, Switch, Row, Col, Typography
} from 'antd';
import { 
  LineChartOutlined,
  CarOutlined,
  ClockCircleOutlined,
  HeatMapOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import OTRPerformanceOverviewTab from './OTRPerformanceOverviewTab';
import OTRDailyOverviewTab from './OTRDailyOverviewTab';
import OTRTripPerformanceTab from './OTRTripPerformanceTab';
import OTRStopPerformanceTab from './OTRStopPerformanceTab';
import OTRDriverPerformanceTab from './OTRDriverPerformanceTab';
import otrDataProcessor from '../utils/OTRDataProcessor';

const { TabPane } = Tabs;
const { Text } = Typography;

// View level constants
const VIEW_LEVELS = {
  TRIP_AGGREGATE: 1,   // Level 1: Aggregate trip performance
  TRIP_BY_DATE: 2,     // Level 2: Trip performance by date
  TRIP_DETAIL: 3,      // Level 3: Trip detail (stop-by-stop)
  STOP_DETAIL: 4       // Level 4: Stop detail (showing trips for a specific stop)
};

const OTRTab = ({
  // State props
  loading,
  showImporter,
  tripData,
  stopData,
  driverData,
  filteredData,
  viewLevel,
  selectedTrip,
  selectedDate,
  selectedStop,
  selectedStopTypes,
  activeTab,
  dateRange,
  tripStartTimes,
  reportData,
  dateFilteredDataset,
  isDateDatasetLoaded,
  treatSchoolOutEarlyAsOnTime,
  
  // Setter props
  setActiveTab,
  setViewLevel,
  setSelectedTrip,
  setSelectedDate,
  setFilteredData,
  setSelectedStopTypes,
  setTreatSchoolOutEarlyAsOnTime,
  
  // Function props
  handleTripClick,
  handleDateClick,
  handleBack,
  handleStopClick,
  filterDataByStopType,
  shouldTreatEarlyAsOnTime,
  
  // Components that need to be passed
  OTRImporter,
  renderBreadcrumb,
  OTR_THRESHOLDS
}) => {
  
  // Apply OTR-specific filtering to the globally filtered data
  const applyOTRFiltering = (data) => {
    if (!data || data.length === 0) return data;
    
    let filteredData = [...data];
    
    // Apply stop type filtering
    filteredData = filterDataByStopType(filteredData);
    
    // // Filter by tracked data if enabled
    // if (showTrackedOnly) {
    //   const beforeTrackedFilter = filteredData.length;
      
    //   // Debug: Analyze the tracking data distribution
    //   let trackedCount = 0;
    //   let untrackedCount = 0;
    //   let partiallyTrackedCount = 0;
      
    //   filteredData.forEach(item => {
    //     const hasActualDeparture = Number.isFinite(item.ACTUAL_DEPARTURE_TIME) || Number.isFinite(item.A_DEP);
    //     const hasActualArrival = Number.isFinite(item.ACTUAL_ARRIVAL_TIME) || Number.isFinite(item.A_ARR);
        
    //     if (hasActualDeparture && hasActualArrival) {
    //       trackedCount++;
    //     } else if (hasActualDeparture || hasActualArrival) {
    //       partiallyTrackedCount++;
    //     } else {
    //       untrackedCount++;
    //     }
    //   });
      
    //   console.log(`OTR Tracking analysis: Fully tracked: ${trackedCount}, Partially tracked: ${partiallyTrackedCount}, Untracked: ${untrackedCount}`);
      
    //   filteredData = filteredData.filter(item => {
    //     // Check for both compact and full field names
    //     const hasActualDeparture = Number.isFinite(item.ACTUAL_DEPARTURE_TIME) || Number.isFinite(item.A_DEP);
    //     const hasActualArrival = Number.isFinite(item.ACTUAL_ARRIVAL_TIME) || Number.isFinite(item.A_ARR);
    //     return hasActualDeparture || hasActualArrival;
    //   });
    //   console.log(`OTR Tracked filter: ${beforeTrackedFilter} -> ${filteredData.length} records (showTrackedOnly: ${showTrackedOnly})`);
    // } else {
    //   console.log(`OTR: No tracked filtering applied (showTrackedOnly: ${showTrackedOnly})`);
    // }
    
    return filteredData;
  };
  
  // Apply OTR filtering to stop data
  const otrFilteredStopData = React.useMemo(() => {
    return applyOTRFiltering(stopData);
  }, [stopData, selectedStopTypes]);
  
  // Debug: Check data flow
  React.useEffect(() => {
    console.log('OTRTab - stopData:', stopData?.length || 0, 'records');
    console.log('OTRTab - otrFilteredStopData:', otrFilteredStopData?.length || 0, 'records');
    if (stopData?.length > 0) {
      console.log('OTRTab - Sample stopData record:', stopData[0]);
    }
  }, [stopData, otrFilteredStopData]);
  
  // Apply OTR filtering to trip data (for trip performance tab)
  const otrFilteredTripData = React.useMemo(() => {
    // Reprocess trips with filtered stop data to ensure trip stats match the selected stop types
    if (otrFilteredStopData && otrFilteredStopData.length > 0) {
      return otrDataProcessor.processTripsData(otrFilteredStopData, treatSchoolOutEarlyAsOnTime);
    }
    // Fallback to original trip data
    return tripData;
  }, [tripData, otrFilteredStopData, treatSchoolOutEarlyAsOnTime]);
  
  // Render OTR-specific filters
  const renderOTRFilters = () => {
    return (
      <Row style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fafafa', borderRadius: '6px' }}>
        <Col flex="auto" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Text strong style={{ marginRight: '8px' }}>Stop Types:</Text>
            <Tag.CheckableTag
              checked={selectedStopTypes.includes('ALL')}
              onChange={checked => {
                if (checked) {
                  setSelectedStopTypes(['ALL']);
                } else if (selectedStopTypes.length === 1 && selectedStopTypes[0] === 'ALL') {
                  setSelectedStopTypes(['F']); 
                }
              }}
              style={{ 
                borderRadius: '6px', 
                padding: '5px 10px',
                height: '32px',
                lineHeight: '22px',
                fontSize: '14px',
                backgroundColor: selectedStopTypes.includes('ALL') ? '#1890ff' : undefined,
                color: selectedStopTypes.includes('ALL') ? 'white' : undefined,
                border: selectedStopTypes.includes('ALL') ? 'none' : '1px solid #d9d9d9',
                cursor: 'pointer',
                marginRight: '4px'
              }}
            >
              All Stops
            </Tag.CheckableTag>
            <Tag.CheckableTag
              checked={selectedStopTypes.includes('F')}
              onChange={checked => {
                let newSelection = [...selectedStopTypes];
                newSelection = newSelection.filter(t => t !== 'ALL');
                
                if (checked) {
                  if (!newSelection.includes('F')) newSelection.push('F');
                } else {
                  newSelection = newSelection.filter(t => t !== 'F');
                }
                
                // If only one item was selected and we're unchecking it, select ALL
                if (!checked && newSelection.length === 0 && selectedStopTypes.length === 1) {
                  setSelectedStopTypes(['ALL']);
                } else {
                  setSelectedStopTypes(newSelection.length ? newSelection : ['F']);
                }
              }}
              style={{ 
                borderRadius: '6px', 
                padding: '5px 10px',
                height: '32px',
                lineHeight: '22px',
                fontSize: '14px',
                backgroundColor: selectedStopTypes.includes('F') ? '#1890ff' : undefined,
                color: selectedStopTypes.includes('F') ? 'white' : undefined,
                border: selectedStopTypes.includes('F') ? 'none' : '1px solid #d9d9d9',
                cursor: 'pointer',
                marginRight: '4px'
              }}
            >
              First
            </Tag.CheckableTag>
            
            <Tag.CheckableTag
              checked={selectedStopTypes.includes('M')}
              onChange={checked => {
                let newSelection = [...selectedStopTypes];
                newSelection = newSelection.filter(t => t !== 'ALL');
                
                if (checked) {
                  if (!newSelection.includes('M')) newSelection.push('M');
                } else {
                  newSelection = newSelection.filter(t => t !== 'M');
                }
                
                // If only one item was selected and we're unchecking it, select ALL
                if (!checked && newSelection.length === 0 && selectedStopTypes.length === 1) {
                  setSelectedStopTypes(['ALL']);
                } else {
                  setSelectedStopTypes(newSelection.length ? newSelection : ['F']);
                }
              }}
              style={{ 
                borderRadius: '6px', 
                padding: '5px 10px',
                height: '32px',
                lineHeight: '22px',
                fontSize: '14px',
                backgroundColor: selectedStopTypes.includes('M') ? '#1890ff' : undefined,
                color: selectedStopTypes.includes('M') ? 'white' : undefined,
                border: selectedStopTypes.includes('M') ? 'none' : '1px solid #d9d9d9',
                cursor: 'pointer',
                marginRight: '4px'
              }}
            >
              Mid
            </Tag.CheckableTag>
            
            <Tag.CheckableTag
              checked={selectedStopTypes.includes('L')}
              onChange={checked => {
                let newSelection = [...selectedStopTypes];
                newSelection = newSelection.filter(t => t !== 'ALL');
                
                if (checked) {
                  if (!newSelection.includes('L')) newSelection.push('L');
                } else {
                  newSelection = newSelection.filter(t => t !== 'L');
                }
                
                // If only one item was selected and we're unchecking it, select ALL
                if (!checked && newSelection.length === 0 && selectedStopTypes.length === 1) {
                  setSelectedStopTypes(['ALL']);
                } else {
                  setSelectedStopTypes(newSelection.length ? newSelection : ['F']);
                }
              }}
              style={{ 
                borderRadius: '6px', 
                padding: '5px 10px',
                height: '32px',
                lineHeight: '22px',
                fontSize: '14px',
                backgroundColor: selectedStopTypes.includes('L') ? '#1890ff' : undefined,
                color: selectedStopTypes.includes('L') ? 'white' : undefined,
                border: selectedStopTypes.includes('L') ? 'none' : '1px solid #d9d9d9',
                cursor: 'pointer'
              }}
            >
              Last
            </Tag.CheckableTag>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* <Switch
              checked={showTrackedOnly}
              onChange={setShowTrackedOnly}
              checkedChildren="Tracked Only"
              unCheckedChildren="Include Untracked"
            /> */}
            
            <Switch
              checked={treatSchoolOutEarlyAsOnTime}
              onChange={setTreatSchoolOutEarlyAsOnTime}
              checkedChildren="School PM Early=OnTime"
              unCheckedChildren="Standard OTR Rules"
            />
          </div>
        </Col>
      </Row>
    );
  };
  
  // Render the current view based on active tab and level
  const renderContent = () => {
    if (loading) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '20px' }}>
            Loading OTR data...
          </div>
        </div>
      );
    }

    if (showImporter) {
      return OTRImporter;
    }

    if (tripData.length === 0) {
      return (
        <Empty 
          description="No OTR data available. Please import data first."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    // Performance Overview Tab
    if (activeTab === 'overview') {
      return (
        <OTRPerformanceOverviewTab
          key={`overview-${selectedStopTypes.join(',')}`}
          stopData={otrFilteredStopData}
          selectedStopTypes={selectedStopTypes}
          setSelectedStopTypes={setSelectedStopTypes}
          dateRange={dateRange}
          tripData={tripData}
          shouldTreatEarlyAsOnTime={shouldTreatEarlyAsOnTime}
          treatSchoolOutEarlyAsOnTime={treatSchoolOutEarlyAsOnTime}
        />
      );
    }
    
    // Daily Overview Tab
    if (activeTab === 'dailyOverview') {
      return (
        <OTRDailyOverviewTab 
          data={otrFilteredStopData}
          selectedStopTypes={selectedStopTypes}
          thresholds={OTR_THRESHOLDS}
          onStopTypeChange={setSelectedStopTypes}
        />
      );
    }

    // Trip tab and levels
    if (activeTab === 'trip') {
      return (
        <OTRTripPerformanceTab
          tripData={otrFilteredTripData}
          stopData={otrFilteredStopData}
          filteredData={filteredData}
          viewLevel={viewLevel}
          selectedTrip={selectedTrip}
          selectedDate={selectedDate}
          selectedStop={selectedStop}
          tripStartTimes={tripStartTimes}
          onTripClick={handleTripClick}
          onDateClick={handleDateClick}
          onBack={handleBack}
          reportData={reportData}
          dateFilteredDataset={dateFilteredDataset}
          isDateDatasetLoaded={isDateDatasetLoaded}
        />
      );
    }
    
    // Stop Map View
    if (activeTab === 'stop') {
      return (
        <OTRStopPerformanceTab
          stopData={otrFilteredStopData}
          onStopClick={handleStopClick}
          tripData={tripData}
          tripStartTimes={tripStartTimes}
          treatSchoolOutEarlyAsOnTime={treatSchoolOutEarlyAsOnTime}
        />
      );
    }
    
    // Driver Performance View
    if (activeTab === 'driver') {
      return (
        <OTRDriverPerformanceTab
          driverData={driverData}
          filteredStopData={filteredData}
          tripData={tripData}
          tripStartTimes={tripStartTimes}
        />
      );
    }

    // Default fallback
    return (
      <Empty 
        description="Select a tab to view data" 
        image={Empty.PRESENTED_IMAGE_SIMPLE} 
      />
    );
  };

  return (
    <Card>        
      {/* OTR-specific filters */}
      {renderOTRFilters()}
      
      <Tabs 
        activeKey={activeTab}
        onChange={key => {
          setActiveTab(key);
          if (key === 'trip' && viewLevel !== VIEW_LEVELS.TRIP_AGGREGATE) {
            // Reset to level 1 when switching to trip tab
            setViewLevel(VIEW_LEVELS.TRIP_AGGREGATE);
            setSelectedTrip(null);
            setSelectedDate(null);
            setFilteredData(tripData);
          }
        }}
        size="large"
        tabBarStyle={{ marginBottom: '20px' }}
      >
        <TabPane 
          tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><LineChartOutlined style={{ marginRight: '12px' }} />Performance Overview</span>} 
          key="overview"
        >
          {activeTab === 'overview' && renderContent()}
        </TabPane>
        
        <TabPane 
          tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><CarOutlined style={{ marginRight: '12px' }} />Trip Performance</span>} 
          key="trip"
        >
          {activeTab === 'trip' && (
            <div>
              {viewLevel > VIEW_LEVELS.TRIP_AGGREGATE && renderBreadcrumb()}
              {renderContent()}
            </div>
          )}
        </TabPane>
      
        <TabPane 
          tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><ClockCircleOutlined style={{ marginRight: '12px' }} />Daily Performance</span>} 
          key="dailyOverview"
        >
          {activeTab === 'dailyOverview' && renderContent()}
        </TabPane>              
        
        <TabPane 
          tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><HeatMapOutlined style={{ marginRight: '12px' }} />Stop Performance</span>} 
          key="stop"
        >
          {activeTab === 'stop' && (
            <div>
              {viewLevel === VIEW_LEVELS.STOP_DETAIL && renderBreadcrumb()}
              {renderContent()}
            </div>
          )}
        </TabPane>
        
        <TabPane 
          tab={<span style={{ fontSize: '16px', padding: '0 8px' }}><BarChartOutlined style={{ marginRight: '12px' }} />Driver Performance</span>} 
          key="driver"
        >
          {activeTab === 'driver' && renderContent()}
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default OTRTab; 